import { A2A_TRANSFERS_REDUCER_KEY, A2A_TRANSFERS_VERSION_NUMBER } from 'app-components/flux/a2aTransfers';
import { ACCOUNTS_REDUCER_KEY } from 'app-components/flux/accounts';
import { ACCOUNTS_BALANCES_REDUCER_KEY } from 'app-components/flux/accounts-balances';
import { ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY } from 'app-components/flux/accounts-balances-history';
import { ACCOUNTS_VALUE_CHANGES_REDUCER_KEY } from 'app-components/flux/accounts-value-changes';
import { ACHIEVEMENTS_REDUCER_KEY } from 'app-components/flux/achievements';
import { REDUCER_KEY as ALERT_RULES_REDUCER_KEY, ALERT_RULES_VERSION_NUMBER } from 'app-components/flux/alert-rules';
import { ALERTS_REDUCER_KEY } from 'app-components/flux/alerts';
import { AUTH_REDUCER_KEY } from 'app-components/flux/auth';
import { BILL_PRESENTMENT_REDUCER_KEY, BILL_PRESENTMENT_VERSION } from 'app-components/flux/bill-presentment';
import { CATEGORIES_REDUCER_KEY, CATEGORIES_VERSION_NUMBER } from 'app-components/flux/categories';
import { CUSTOMERS_VERSION_NUMBER } from 'app-components/flux/customers';
import { CUSTOMERS_REDUCER_KEY } from 'app-components/flux/customers/customersTypes';
import { DATASETS_REDUCER_KEY } from 'app-components/flux/datasets';
import { DOCUMENTS_REDUCER_KEY } from 'app-components/flux/documents';
import {
  DOMAIN_LOGOS_REDUCER_KEY,
  DOMAIN_LOGOS_VERSION_NUMBER,
} from 'app-components/flux/domain-logos/domainLogosTypes';
import { ENTITLEMENTS_REDUCER_KEY } from 'app-components/flux/entitlements';
import { FEATURE_FLAGS_REDUCER_KEY } from 'app-components/flux/feature-flags';
import { FILTERS_REDUCER_KEY } from 'app-components/flux/filters';
import { FREE_TO_SPEND_REDUCER_KEY } from 'app-components/flux/free-to-spend';
import { GOALS_REDUCER_KEY } from 'app-components/flux/goals';
import {
  INSTITUTION_LOGINS_REDUCER_KEY,
  INSTITUTION_LOGINS_VERSION_NUMBER,
  institutionLoginsTransformers,
} from 'app-components/flux/institution-logins';
import { INSTITUTIONS_REDUCER_KEY } from 'app-components/flux/institutions';
import {
  INVESTMENT_HOLDINGS_V2_REDUCER_KEY,
  INVESTMENT_HOLDINGS_V2_VERSION_NUMBER,
} from 'app-components/flux/investment-holdings-v2';
import { INVESTMENT_IRR_REDUCER_KEY } from 'app-components/flux/investment-irr';
import { INVESTMENT_QUOTES_V2_REDUCER_KEY } from 'app-components/flux/investment-quotes-v2';
import { INVESTMENT_SYMBOLS_KEY } from 'app-components/flux/investment-symbols';
import { INVESTMENT_TWRR_REDUCER_KEY } from 'app-components/flux/investment-twrr';
import { INVESTMENTS_NEWSFEED_REDUCER_KEY } from 'app-components/flux/investments-newsfeed';
import { MILESTONE_REDUCER_KEY } from 'app-components/flux/milestones';
import { POSTPONED_ACTIONS_REDUCER_KEY } from 'app-components/flux/postponed-actions';
import { PREFERENCES_V2_REDUCER_KEY } from 'app-components/flux/preferences-v2';
import { PROFILE_REDUCER_KEY, PROFILE_VERSION_NUMBER } from 'app-components/flux/profile';
import { PROJECTS_REDUCER_KEY } from 'app-components/flux/projects/projectsTypes';
import {
  SCHEDULED_TRANSACTIONS_REDUCER_KEY,
  SCHEDULED_TRANSACTIONS_VERSION_NUMBER,
} from 'app-components/flux/scheduled-transactions';
import { SECURITIES_REDUCER_KEY } from 'app-components/flux/securities';
import { SPENDING_WATCH_LIST_REDUCER_KEY } from 'app-components/flux/spending-watchlist';
import { SUBSCRIPTIONS_REDUCER_KEY } from 'app-components/flux/subscriptions';
import { TAGS_REDUCER_KEY } from 'app-components/flux/tags';
import { TAXES_REDUCER_KEY, TAXES_VERSION } from 'app-components/flux/taxes/taxesTypes';
import {
  TICKER_LOGOS_REDUCER_KEY,
  TICKER_LOGOS_VERSION_NUMBER,
} from 'app-components/flux/ticker-logos/tickerLogosTypes';
import { TRANSACTIONS_REDUCER_KEY, TRANSACTIONS_VERSION_NUMBER } from 'app-components/flux/transactions';
import type { PersistableStateConfig } from 'app-components/utils/persist';
import { initializeConfig } from 'app-components/utils/persist';

import { APP_REDUCER_KEY } from 'data/app/reducer';
import { FUNDING_ACCOUNTS_REDUCER_KEY } from 'data/fundingAccounts/reducer';
import { REDUCER_KEY as PREFERENCES_REDUCER_KEY } from 'data/preferences/reducer';
import { TRANSFER_BALANCE_REDUCER_KEY } from 'data/transferBalance/transferBalanceReducer';
import { TRANSFER_HISTORY_REDUCER_KEY } from 'data/transferHistory/transferHistoryReducer';

import localForage from 'localforage';

export function createPersistConfig(userId: string, datasetId: string) {
  const persistConfig = {
    version: 2,
    cacheMetadataKey: 'simplifiCacheMeta',
    encryptionKey: `${userId}:${datasetId}`,
    storageInterface: localForage,
    getCacheKeyForStateKey: (stateKey: string) => `${userId}:${datasetId}:${stateKey}`,
    persistableStateConfig: {
      [ACCOUNTS_BALANCES_REDUCER_KEY]: {
        version: 1,
      },
      [A2A_TRANSFERS_REDUCER_KEY]: { version: A2A_TRANSFERS_VERSION_NUMBER },
      [ACCOUNTS_BALANCES_HISTORY_REDUCER_KEY]: { version: 1 },
      [ACCOUNTS_REDUCER_KEY]: { version: 1 },
      [ACCOUNTS_VALUE_CHANGES_REDUCER_KEY]: { version: 1 },
      [ACHIEVEMENTS_REDUCER_KEY]: { version: 1 },
      [ALERTS_REDUCER_KEY]: { version: 2 },
      [ALERT_RULES_REDUCER_KEY]: { version: ALERT_RULES_VERSION_NUMBER },
      [APP_REDUCER_KEY]: { version: 1 },
      [AUTH_REDUCER_KEY]: {
        version: 1,
        encrypt: true,
      },
      [BILL_PRESENTMENT_REDUCER_KEY]: { version: BILL_PRESENTMENT_VERSION },
      [CATEGORIES_REDUCER_KEY]: { version: CATEGORIES_VERSION_NUMBER },
      [CUSTOMERS_REDUCER_KEY]: { version: CUSTOMERS_VERSION_NUMBER },
      [DATASETS_REDUCER_KEY]: { version: 1 },
      [DOCUMENTS_REDUCER_KEY]: { version: 1 },
      [DOMAIN_LOGOS_REDUCER_KEY]: {
        version: DOMAIN_LOGOS_VERSION_NUMBER,
      },
      [ENTITLEMENTS_REDUCER_KEY]: {
        version: 1,
        encrypt: true,
      },
      [FEATURE_FLAGS_REDUCER_KEY]: { version: 1 },
      [FILTERS_REDUCER_KEY]: { version: 1 },
      [FREE_TO_SPEND_REDUCER_KEY]: { version: 1 },
      [FUNDING_ACCOUNTS_REDUCER_KEY]: { version: 2 },
      [GOALS_REDUCER_KEY]: { version: 2 },
      [INSTITUTIONS_REDUCER_KEY]: { version: 1 },
      [INSTITUTION_LOGINS_REDUCER_KEY]: {
        version: INSTITUTION_LOGINS_VERSION_NUMBER,
        encrypt: true,
        stateToCache: institutionLoginsTransformers.stateToCacheInstitutionLogins,
        cacheToState: institutionLoginsTransformers.cacheToStateInstitutionLogins,
      },
      [INVESTMENTS_NEWSFEED_REDUCER_KEY]: { version: 1 },
      [INVESTMENT_HOLDINGS_V2_REDUCER_KEY]: { version: INVESTMENT_HOLDINGS_V2_VERSION_NUMBER },
      [INVESTMENT_IRR_REDUCER_KEY]: { version: 1 },
      [INVESTMENT_QUOTES_V2_REDUCER_KEY]: { version: 1 },
      [INVESTMENT_SYMBOLS_KEY]: { version: 1 },
      [INVESTMENT_TWRR_REDUCER_KEY]: { version: 1 },
      [TICKER_LOGOS_REDUCER_KEY]: { version: TICKER_LOGOS_VERSION_NUMBER },
      [MILESTONE_REDUCER_KEY]: { version: 1 },
      [POSTPONED_ACTIONS_REDUCER_KEY]: { version: 1 },
      [PREFERENCES_REDUCER_KEY]: { version: 1 },
      [PREFERENCES_V2_REDUCER_KEY]: { version: 1 },
      [PROFILE_REDUCER_KEY]: {
        version: PROFILE_VERSION_NUMBER,
        encrypt: true,
      },
      [PROJECTS_REDUCER_KEY]: { version: 1 },
      [SCHEDULED_TRANSACTIONS_REDUCER_KEY]: { version: SCHEDULED_TRANSACTIONS_VERSION_NUMBER },
      [SECURITIES_REDUCER_KEY]: { version: 1 },
      [SPENDING_WATCH_LIST_REDUCER_KEY]: { version: 1 },
      [SUBSCRIPTIONS_REDUCER_KEY]: { version: 1 },
      [TAGS_REDUCER_KEY]: { version: 1 },
      [TAXES_REDUCER_KEY]: { version: TAXES_VERSION },
      [TRANSACTIONS_REDUCER_KEY]: { version: TRANSACTIONS_VERSION_NUMBER },
      [TRANSFER_BALANCE_REDUCER_KEY]: { version: 1 },
      [TRANSFER_HISTORY_REDUCER_KEY]: { version: 1 },
    } satisfies PersistableStateConfig,
  };

  return initializeConfig(persistConfig);
}
